import {
  Button,
  Card,
  CardBody,
  CardProps,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  RiAddLine,
  RiCheckboxCircleFill,
  RiLock2Line,
  RiPercentLine,
} from "@remixicon/react";
import { AnimatePresence } from "framer-motion";
import { ReactNode, useCallback, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { Link } from "react-router";

import { AndOrList } from "@/common/components/AndOrList";
import CardContainer from "@/common/components/CardContainer";
import FormSwitchButtonGroup from "@/common/components/Form/FormSwitchButtonGroup";
import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import MotionBox, { MotionBoxProps } from "@/common/components/MotionBox";
import { MotionRule } from "@/common/components/MotionComponents";
import {
  RuleAdvancedDisplay,
  RuleAdvancedForm,
} from "@/common/components/Rule/RuleAdvancedForm";
import { RulesEstimationSingle } from "@/common/components/Rule/RulesEstimationSingle";
import {
  RuleSimpleDisplay,
  RuleSimpleForm,
} from "@/common/components/Rule/RuleSimpleForm";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useSegments } from "@/company/data/useSegments";
import { FeatureName } from "@reflag/shared/featureAPI";
import {
  CustomRule,
  FeatureOptInMemberships,
  FeatureTargeting,
  UpdateEnvironmentFeatureTargeting,
} from "@reflag/shared/featureTargetingAPI";
import {
  allCompaniesSegmentFilter,
  alwaysTrueFilter,
  COMPANY_ID_CONTEXT_FIELD,
  MAX_ROLLOUT_THRESHOLD,
} from "@reflag/shared/filter";
import { FeatureMonitorUrl } from "@reflag/shared/urls";

const sharedMotionProps: MotionBoxProps = {
  animate: { opacity: 1, height: "auto" },
  display: "flex",
  exit: { opacity: 0, height: 0 },
  flexDirection: "column",
  gap: 1,
  initial: { opacity: 0, height: 0 },
  transition: { duration: "0.15", ease: "easeInOut" },
};

const sharedCardProps: CardProps = {
  p: 4,
  w: "full",
};

type RulesFormProps = {
  feature: FeatureName;
  baseName: string;
  fields: (CustomRule & { id: string })[];
  append: (value: CustomRule) => void;
  remove: (index: number) => void;
  additionalRuleContent?: ReactNode;
  optInMemberships?: FeatureOptInMemberships;
};

export function RulesForm({
  feature,
  baseName,
  fields,
  append,
  remove,
  additionalRuleContent,
  optInMemberships,
}: RulesFormProps) {
  const { env } = useEnvironment();
  const targeting: UpdateEnvironmentFeatureTargeting = useWatch({
    name: baseName,
  });
  const { targetingMode, customRules } = targeting;

  const hasCustomRules = customRules.length > 0;

  const { data: segments } = useSegments();

  const allCompaniesSegment = useMemo(() => {
    return segments?.find((segment) => segment.isAllSegment);
  }, [segments]);

  const addGradualRolloutRule = useCallback(() => {
    if (!allCompaniesSegment) return;
    append({
      partialRolloutThreshold: MAX_ROLLOUT_THRESHOLD / 2,
      partialRolloutContextAttribute: COMPANY_ID_CONTEXT_FIELD,
      filter: allCompaniesSegmentFilter(allCompaniesSegment),
    });
  }, [append, allCompaniesSegment]);

  return (
    <CardContainer gap={1}>
      <Flex direction="row" justify="space-between" pb={1} px={2}>
        <Flex align="center" direction="row" gap={2}>
          <RulesEstimationSingle
            flagKey={feature.key}
            optInMemberships={optInMemberships}
            targeting={targeting}
          />
        </Flex>
        <Flex align="center" direction="row" gap={2}>
          <FormSwitchButtonGroup
            buttons={[
              { label: "No one", id: "none" },
              { label: "Some", id: "some" },
              { label: "Everyone", id: "everyone" },
            ]}
            name={`${baseName}.targetingMode`}
            rules={{
              deps: [baseName],
            }}
          />
        </Flex>
      </Flex>
      <AnimatePresence initial={false}>
        {targetingMode === "everyone" ? (
          <MotionBox {...sharedMotionProps}>
            <Card {...sharedCardProps}>
              <CardBody
                alignItems="center"
                display="flex"
                flexDirection="column"
                gap={4}
                justifyContent="center"
              >
                <HStack spacing={1.5}>
                  <RiCheckboxCircleFill size="16" />
                  <Text as="span">Everyone has access</Text>
                </HStack>
                <Button
                  as={Link}
                  to={FeatureMonitorUrl(env, feature)}
                  variant="outline"
                >
                  Monitor
                </Button>
              </CardBody>
            </Card>
          </MotionBox>
        ) : targetingMode === "some" ? (
          <MotionBox {...sharedMotionProps}>
            <AndOrList
              conjunction="or"
              conjunctionProps={{ ml: 4, zIndex: 1 }}
              direction="vertical"
              gap={0}
            >
              <Card {...sharedCardProps}>
                <RuleSimpleForm
                  baseName={baseName}
                  toolbar={
                    !hasCustomRules && (
                      <Tooltip label="Add percentage rollout rule" hasArrow>
                        <IconButton
                          aria-label="Add percentage rollout rule"
                          color={"dimmed"}
                          icon={<RiPercentLine size={18} />}
                          size="xs"
                          variant={"ghost"}
                          onClick={addGradualRolloutRule}
                        />
                      </Tooltip>
                    )
                  }
                />
              </Card>
              {additionalRuleContent}
              {fields.map(({ id }, index) => (
                <MotionRule key={id}>
                  <Card {...sharedCardProps}>
                    <RuleAdvancedForm
                      baseName={`${baseName}.customRules.${index}`}
                      remove={() => remove(index)}
                    />
                  </Card>
                </MotionRule>
              ))}
            </AndOrList>
            <MotionBox transition={{ duration: 0.1 }} layout>
              <Button
                alignSelf="flex-start"
                leftIcon={<RiAddLine size={16} />}
                size="sm"
                variant="ghostDimmed"
                onClick={() =>
                  append({
                    partialRolloutThreshold: MAX_ROLLOUT_THRESHOLD,
                    partialRolloutContextAttribute: COMPANY_ID_CONTEXT_FIELD,
                    filter: alwaysTrueFilter,
                  })
                }
              >
                Add rule
              </Button>
            </MotionBox>
          </MotionBox>
        ) : null}
      </AnimatePresence>
    </CardContainer>
  );
}

type Props = {
  targeting: FeatureTargeting;
  flagKey: string;
  showLockIcon?: boolean;
  rightElement?: ReactNode;
  lockReason?: string;
  additionalRuleContent?: ReactNode;
  optInMemberships?: FeatureOptInMemberships;
};

export const RulesDisplay = ({
  targeting,
  flagKey,
  rightElement,
  lockReason,
  additionalRuleContent,
  optInMemberships,
}: Props) => {
  const { targetingMode, segmentIds, companyIds, userIds, customRules } =
    targeting;

  const hasSimpleTargeting =
    !!segmentIds.length || !!companyIds.length || !!userIds.length;
  const hasAdvancedTargeting = !!customRules.length;

  const everyoneCheckmarkColor = useColorModeValue("gray.950", "white");

  return (
    <CardContainer>
      <Flex direction="row" justify="space-between" pb={1} px={2}>
        <Flex align="center" direction="row" gap={2}>
          <RulesEstimationSingle
            flagKey={flagKey}
            optInMemberships={optInMemberships}
            targeting={targeting}
          />
        </Flex>
        <Flex align="center" direction="row" gap={2}>
          {lockReason && (
            <InfoIconTooltip
              icon={<RiLock2Line size={16} />}
              text={lockReason}
            />
          )}
          {rightElement}
        </Flex>
      </Flex>
      {targetingMode === "everyone" ? (
        <Card {...sharedCardProps}>
          <HStack spacing={1.5}>
            <RiCheckboxCircleFill color={everyoneCheckmarkColor} size={16} />
            <Text as="span">Everyone has access</Text>
          </HStack>
        </Card>
      ) : targetingMode === "some" &&
        (hasSimpleTargeting ||
          additionalRuleContent ||
          hasAdvancedTargeting) ? (
        <AndOrList
          conjunction="or"
          conjunctionProps={{ ml: 4, zIndex: 1 }}
          direction="vertical"
          gap={0}
        >
          {hasSimpleTargeting && (
            <Card {...sharedCardProps}>
              <RuleSimpleDisplay targeting={targeting} />
            </Card>
          )}
          {additionalRuleContent}
          {customRules.map((targetingRule, i) => (
            <Card key={i} {...sharedCardProps}>
              <RuleAdvancedDisplay targetingRule={targetingRule} />
            </Card>
          ))}
        </AndOrList>
      ) : (
        <Card {...sharedCardProps}>
          <HStack color="dimmed" spacing={1.5}>
            <Text as="span">
              {targetingMode === "none"
                ? "No one has access"
                : "No targeting rules configured"}
            </Text>
          </HStack>
        </Card>
      )}
    </CardContainer>
  );
};
